import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { useTabLabel } from '../../components/shell/TabsContext';
import RecordPage from '../../components/shell/RecordPage';
import { StatTiles } from '../../components/shell/Page';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  ChartBar,
  Users,
  Trophy,
  CheckCircle,
  XCircle,
  DownloadSimple,
  Question,
  TrendUp,
  TrendDown,
  Target,
  Timer,
  Pencil
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import RichHtml from '../../components/richtext/RichText';

// Helper to format seconds
const formatTime = (seconds) => {
  if (!seconds || seconds === 0) return '-';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
};

export default function ExamReport() {
  const { examId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  
  const [report, setReport] = useState(null);
  useTabLabel(report?.exam_title ? `Report: ${report.exam_title}` : null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    fetchReport();
  }, [examId]);

  const fetchReport = async () => {
    try {
      const res = await api.get(`/exams/${examId}/report`);
      setReport(res.data);
    } catch (error) {
      toast.error('Failed to load exam report');
      navigate('/instructor/exams');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    setDownloading(true);
    try {
      const response = await api.get(`/exams/${examId}/report/csv`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `exam_report_${examId}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Report exported successfully');
    } catch (error) {
      toast.error('Failed to export report');
    } finally {
      setDownloading(false);
    }
  };

  if (loading || !report) {
    return (
      <DashboardLayout>
        <RecordPage title={loading ? 'Report' : 'Report not found'} loading={loading} onBack={() => navigate('/instructor/exams')} maxWidth="none">
          <p className="text-muted-foreground text-center py-12">Report not found</p>
        </RecordPage>
      </DashboardLayout>
    );
  }

  // Calculate score distribution for chart
  const maxDistribution = Math.max(...Object.values(report.score_distribution || {}), 1);

  return (
    <DashboardLayout>
      <RecordPage
        data-testid="exam-report-page"
        title={`Report: ${report.exam_title}`}
        subtitle={report.total_attempts === 0
          ? 'Exam performance report · no attempts yet'
          : `${report.total_attempts} attempt${report.total_attempts === 1 ? '' : 's'} by ${report.unique_learners} learner${report.unique_learners === 1 ? '' : 's'} · ${report.pass_rate}% pass rate · passing score ${report.passing_score}%`}
        onBack={() => navigate('/instructor/exams')}
        onRefresh={fetchReport}
        maxWidth="none"
        primary={[
          { key: 'export', label: downloading ? 'Exporting...' : 'Export CSV', icon: DownloadSimple, onClick: handleExportCSV, disabled: downloading || report.total_attempts === 0, testid: 'export-csv-btn' },
        ]}
        secondary={[
          { key: 'edit', label: 'Edit exam', icon: Pencil, onClick: () => navigate(`/instructor/exams/${examId}`) },
        ]}
      >
        {report.total_attempts === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <ChartBar className="w-16 h-16 mx-auto mb-4 text-muted-foreground/30" />
              <h3 className="text-lg font-semibold mb-2">No Attempts Yet</h3>
              <p className="text-muted-foreground">
                This exam hasn't been attempted by any learners yet.
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Summary tiles */}
            <StatTiles
              items={[
                { label: 'Learners', value: report.unique_learners, icon: Users },
                { label: 'Total attempts', value: report.total_attempts, icon: ChartBar },
                { label: 'Pass rate', value: `${report.pass_rate}%`, icon: Trophy },
                { label: 'Avg score', value: `${report.average_score}%`, icon: Target },
                { label: 'Highest', value: `${report.highest_score}%`, icon: TrendUp },
                { label: 'Avg time', value: formatTime(report.average_time_seconds), icon: Timer },
              ]}
            />

            <Tabs defaultValue="attempts" className="space-y-4">
              <TabsList>
                <TabsTrigger value="attempts">All Attempts</TabsTrigger>
                <TabsTrigger value="questions">Question Analysis</TabsTrigger>
                <TabsTrigger value="distribution">Score Distribution</TabsTrigger>
              </TabsList>

              {/* Attempts Tab */}
              <TabsContent value="attempts">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Student Attempts</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Student</th>
                            <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Attempt</th>
                            <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Score</th>
                            <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Result</th>
                            <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Time</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Submitted</th>
                          </tr>
                        </thead>
                        <tbody>
                          {report.attempts.map((attempt) => (
                            <tr key={attempt.attempt_id} className="border-b last:border-0 hover:bg-muted/50">
                              <td className="py-3 px-4">
                                <div>
                                  <p className="font-medium">{attempt.user_name}</p>
                                  <p className="text-xs text-muted-foreground">{attempt.user_email}</p>
                                </div>
                              </td>
                              <td className="py-3 px-4 text-center">
                                <Badge variant="outline">#{attempt.attempt_number}</Badge>
                              </td>
                              <td className="py-3 px-4 text-center">
                                <div className="flex flex-col items-center gap-1">
                                  <span className="font-bold">{attempt.percentage}%</span>
                                  <span className="text-xs text-muted-foreground">
                                    {attempt.score}/{attempt.max_score}
                                  </span>
                                </div>
                              </td>
                              <td className="py-3 px-4 text-center">
                                {attempt.passed ? (
                                  <Badge className="bg-emerald-600">
                                    <CheckCircle className="w-3 h-3 mr-1" />
                                    Passed
                                  </Badge>
                                ) : (
                                  <Badge variant="destructive">
                                    <XCircle className="w-3 h-3 mr-1" />
                                    Failed
                                  </Badge>
                                )}
                              </td>
                              <td className="py-3 px-4 text-center text-sm">
                                {formatTime(attempt.time_taken_seconds)}
                              </td>
                              <td className="py-3 px-4 text-sm text-muted-foreground">
                                {attempt.submitted_at ? new Date(attempt.submitted_at).toLocaleString() : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Question Analysis Tab */}
              <TabsContent value="questions">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Question className="w-5 h-5" />
                      Question Performance Analysis
                    </CardTitle>
                    <p className="text-sm text-muted-foreground">
                      Questions sorted by difficulty (lowest accuracy first)
                    </p>
                  </CardHeader>
                  <CardContent>
                    {report.question_analysis.length === 0 ? (
                      <p className="text-center text-muted-foreground py-8">
                        No question data available yet
                      </p>
                    ) : (
                      <div className="space-y-4">
                        {report.question_analysis.map((q, idx) => (
                          <div 
                            key={q.question_id} 
                            className={`p-4 rounded-lg border ${
                              q.accuracy_rate < 50 
                                ? 'border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-900/20' 
                                : q.accuracy_rate < 70 
                                  ? 'border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-900/20'
                                  : 'border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-900/20'
                            }`}
                          >
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <Badge variant="outline" className="text-xs">Q{idx + 1}</Badge>
                                  {q.accuracy_rate < 50 && (
                                    <Badge variant="destructive" className="text-xs">
                                      <TrendDown className="w-3 h-3 mr-1" />
                                      Difficult
                                    </Badge>
                                  )}
                                </div>
                                <RichHtml html={q.question_text} className="text-sm" />
                              </div>
                              <div className="text-right flex-shrink-0">
                                <p className={`text-2xl font-bold ${
                                  q.accuracy_rate < 50 ? 'text-red-600' :
                                  q.accuracy_rate < 70 ? 'text-amber-600' : 'text-green-600'
                                }`}>
                                  {q.accuracy_rate}%
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {q.correct_count}/{q.total_attempts} correct
                                </p>
                              </div>
                            </div>
                            <div className="mt-3">
                              <Progress 
                                value={q.accuracy_rate} 
                                className={`h-2 ${
                                  q.accuracy_rate < 50 ? '[&>div]:bg-red-500' :
                                  q.accuracy_rate < 70 ? '[&>div]:bg-amber-500' : '[&>div]:bg-green-500'
                                }`}
                              />
                            </div>
                            <p className="text-xs text-muted-foreground mt-2">
                              Avg time: {formatTime(q.average_time_seconds)}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Score Distribution Tab */}
              <TabsContent value="distribution">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Score Distribution</CardTitle>
                    <p className="text-sm text-muted-foreground">
                      Passing score: {report.passing_score}%
                    </p>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {Object.entries(report.score_distribution || {}).map(([range, count]) => {
                        const rangeStart = parseInt(range.split('-')[0]);
                        const isPassing = rangeStart >= report.passing_score;
                        
                        return (
                          <div key={range} className="flex items-center gap-4">
                            <span className="w-16 text-sm text-muted-foreground">{range}%</span>
                            <div className="flex-1 h-8 bg-muted rounded-full overflow-hidden">
                              <div 
                                className={`h-full rounded-full transition-all ${
                                  isPassing ? 'bg-emerald-500' : 'bg-red-400'
                                }`}
                                style={{ width: `${(count / maxDistribution) * 100}%` }}
                              />
                            </div>
                            <span className="w-12 text-sm font-medium text-right">{count}</span>
                          </div>
                        );
                      })}
                    </div>
                    
                    <div className="flex items-center gap-4 mt-6 pt-4 border-t">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded bg-emerald-500" />
                        <span className="text-sm">Passed</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded bg-red-400" />
                        <span className="text-sm">Failed</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </>
        )}
      </RecordPage>
    </DashboardLayout>
  );
}
